import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import ColorSelectButton from 'components/UI/ColorSelectButton'
import { t, getImageProcessorUrl } from 'helpers'
import { usePortfolioStore } from 'store'
import { AttributeSelector, Content, SelectOption } from 'types/app'
import {
	attributeBasicFields,
} from 'pages/Portfolio/fields'
import useRelationsContent from 'hooks/api/useRelationsContent'
import { RELATIONS } from 'types/enums'
import s from './index.module.scss'
interface Props {
	selector: AttributeSelector
}

const ColorsSelector = ({ selector }: Props): JSX.Element => {
	const hairColorSelector =
		selector.name === attributeBasicFields.hair?.selectors?.[1]?.name
	const cardValues = usePortfolioStore(
		useShallow(
			(state) =>
				state.cardValues[state.modalType] ?? state.uploadImageData ?? {},
		),
	)
	const {
		uploadImageData,
	} = usePortfolioStore.getState()
	const { data } = useRelationsContent(
		[RELATIONS.IMAGE, RELATIONS.BEST_PERFORMANCES],
		undefined,
		undefined,
		'',
		false,
		true,
	)
	const value = useMemo(() => {
		const sourceData =
			cardValues?.[selector.name] ??
			(uploadImageData && (uploadImageData as Record<string, any>)[selector.name])

		if (!sourceData) return hairColorSelector ? [] : undefined

		if (hairColorSelector) {
			const parsed =
				typeof sourceData === 'string' && sourceData.startsWith('[')
					? (JSON.parse(sourceData) as string[])
					: (sourceData as string[])

			return selector?.options?.filter((option) =>
				parsed?.includes(option.value),
			)
		} else {
			return selector?.options?.find(
				(option) => sourceData === option.value,
			)
		}
	}, [hairColorSelector, cardValues, uploadImageData, selector.name, selector?.options])

	const images = useMemo(
		() =>
			data
				? Object.entries(data)
					?.filter(([key]) => RELATIONS.IMAGE === key)
					?.map((value) => value?.[1])
					.flat()
				: [],
		[data],
	)

	const videos = useMemo(
		() =>
			data
				? Object.entries(data)
					?.filter(([key]) => RELATIONS.BEST_PERFORMANCES === key)
					?.map((value) => value?.[1])
					.flat()
				: [],
		[data],
	)

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
			}}>
			<span className="caption-m inputLabel">{t(selector.label)}</span>
			<div
				style={{
					width: '100%',
					gap: '12px',
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
				}}>
				{selector?.options?.map((option) => {
					const isChecked = Array.isArray(value)
						? !!value?.find((v) => v?.value === option.value)
						: value?.value === option?.value

					return (
						<div key={option.value} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<ColorSelectButton
								checked={isChecked}
								onChange={() => {
									const { setCardValues, setGalleryType } = usePortfolioStore.getState()
									setGalleryType({
										value: option.value,
										label: option.label,
									})

									let newValue = option.value
									if (hairColorSelector) {
										const array = Array.isArray(value) ? value : []
										const checked = array?.find((v) => v?.value === option.value)
										const newValues = checked
											? array
												?.map((a) => a.value)
												?.filter((v) => v !== option.value)
											: [...array.map((a) => a.value), option.value]

										newValue = JSON.stringify(newValues)
									}

									setCardValues({
										[selector.name]: newValue,
									})
								}}
								label={option.label}
								hint={option.hint}
								color={option.color}
							/>
							{isChecked && (
								<div style={{ marginTop: 4 }}>
									<MediaAssign
										option={option}
										mediaItems={[...images, ...videos]}
									/>
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

const MediaAssign = ({
	option,
	mediaItems,
}: {
	option: SelectOption
	mediaItems?: Content[]
}): JSX.Element => {
	const [type, galleryType, galleryValues] = usePortfolioStore(
		useShallow((state) => [
			state.modalType,
			state.galleryType,
			state.galleryValues,
		]),
	)
	const valueName = 'undefined' + ':' + option?.value

	const selected = mediaItems?.filter((data) =>
		galleryValues[data?.ContentId]?.includes(valueName),
	)

	return (
		<div className={s.additionalSelector}>
			{selected?.length ? (
				<div className={s.mediaAssigned}>
					{selected.map((data) => (
						<div key={data.ContentId} className={s.dotWrapper}>
							<div className={s.yellowDot} />
						</div>
					))}
				</div>
			) : null}
		</div>
	)
}

export default ColorsSelector
